import { FC, useState } from 'react'
import { ApplicationBodyFooter, ApplicationNextButtonsContainer, ButtonWithMessageContainer } from '../defaultComponents'
import Button from '../../basic/Button'
import { useNavigate, useLocation } from 'react-router-dom'
import ApplicantionData from 'src/dtos/applicationData'
import { useApplication } from 'src/context/application'
import { IoMdArrowForward } from 'react-icons/io'
import loaderWhiteImg from 'src/assets/loader-white.svg'
import flagImg from 'src/assets/applicationFormsImgs/flag_with_check.svg'
import warningImg from 'src/assets/warning.svg'
import { object, string, bool, number, ValidationError, array } from 'yup'
import { useErrors } from 'src/context/errors'

interface PrebuiltBodyFooterProps {
  goBackRoute: string;
  nextRoute?: string;
  updatedApplication?: ApplicantionData;
  isFormValid?: boolean;
  isCompleteApplicationAvailable?: boolean;
  beforeSaveContinueClick?: () => void;
  disableSaveandContinue?: boolean;
}

const PrebuiltBodyFooter: FC<PrebuiltBodyFooterProps> = ({
  goBackRoute,
  nextRoute,
  updatedApplication,
  isFormValid,
  isCompleteApplicationAvailable,
  beforeSaveContinueClick,
  disableSaveandContinue = false
}) => {
  const navigate = useNavigate()
  const { search, pathname } = useLocation()
  const { application, saveApplicationForLater, saveApplication } = useApplication()
  const { clearToast } = useErrors()
  const [isCompletingApp, setIsCompletingApp] = useState(false)
  const [buttonsDisabled, setButtonsDisabled] = useState(false)
  const [isActionBeingTaken, setIsActionBeingTaken] = useState<string | undefined>(undefined)
  const [showSaveLaterMessage, setShowSaveLaterMessage] = useState('close-container content-hidden')
  const [showReviewBlockMessage, setShowReviewBlockMessage] = useState('close-container content-hidden')
  const [showDuplicateQuotesMessage, setShowDuplicateQuotesMessage] = useState('close-container content-hidden')

  const reviewMode = search.includes('review')

  const reviewUrl = application?.insuranceType && application?.insuranceType === 'LAWYER' ?
    (search.includes('firm') ? '/review/firm' : search.includes('insurance') ? '/review/insurance' : '/review/attorneys') :
    (search.includes('basic') ? '/judge/review/basic' : '/judge/review/insurances')

  const correctSearchParams = nextRoute?.includes('review') ? '' : search

  const goBack = () => {
    clearToast()
    navigate({
      pathname: goBackRoute,
      search
    })
  }

  const saveLaterClick = async () => {
    setIsActionBeingTaken('SAVE_LATER')
    setButtonsDisabled(true)
    try {
      await saveApplicationForLater({
        ...updatedApplication,
        stoppedAt: reviewMode ? reviewUrl : pathname
      })
      setShowSaveLaterMessage('open-container')
    } catch (_) { } finally {
      setIsActionBeingTaken(undefined)
      setButtonsDisabled(false)
    }
  }

  const saveContinueClick = async () => {
    beforeSaveContinueClick && beforeSaveContinueClick()
    if (isFormValid === undefined || isFormValid === true) {
      setIsActionBeingTaken('SAVE_CONTINUE')
      setButtonsDisabled(true)
      try {
        let redirectUrl = await saveApplication({
          ...updatedApplication,
          stoppedAt: reviewMode ? reviewUrl : nextRoute
        })

        if(!!redirectUrl){
          navigate({
            pathname: redirectUrl,
            search
          })
        }else{
          navigate({
            pathname: nextRoute,
            search: correctSearchParams
          })
        }
        
      } catch (_) { } finally {
        setIsActionBeingTaken(undefined)
        setButtonsDisabled(false)
      }
    } else {
      if(pathname.includes('/quote/select')) {
        setShowDuplicateQuotesMessage('open-container')
      }
    }
  }

  const returnReviewValidationSchema = object({
    realEstate: bool().required(),
    realEstateInfo: object().when('realEstate', {
      is: true,
      then: sch => sch.shape({
        hasFirmMemberWithEscrowLicense: bool().required('REAL_ESTATE'),
        hasAgreementWithTitleCompanies: bool().when('hasFirmMemberWithEscrowLicense', {
          is: true,
          then: sch => sch.required('REAL_ESTATE'),
          otherwise: sch => sch.optional().nullable()
        }),
        agreementWithTitleCompaniesFile: array().when('hasAgreementWithTitleCompanies', {
          is: true,
          then: sch => sch.min(1, 'REAL_ESTATE').required('REAL_ESTATE'),
          otherwise: sch => sch.optional().nullable()
        }),
        hasFirmMemberFeeAttorney: bool().required('REAL_ESTATE'),
        hasFirmMemberOutsideClosingAttorney: bool().required('REAL_ESTATE'),
        nonAttorneysWithEscrowLicenseQuantity: number().required('REAL_ESTATE'),
      }).typeError('REAL_ESTATE').required('REAL_ESTATE'),
      otherwise: sch => sch.optional().nullable()
    }),
    securitiesLaw: bool().required(),
    securitiesLawInfo: object().when('securitiesLaw', {
      is: true,
      then: sch => sch.shape({
        conductsDueDiligenceInvestigation: bool().required('SECURITIES_LAW'),
        involvedInActionByRegulator: bool().required('SECURITIES_LAW'),
        involvedInActionByRegulatorDescription: string().when('involvedInActionByRegulator', {
          is: true,
          then: sch => sch.required('SECURITIES_LAW'),
          otherwise: sch => sch.optional().nullable()
        }),
        hasPreparedPortionOfProspectus: bool().required('SECURITIES_LAW'),
        involvedInDirectSale: bool().required('SECURITIES_LAW'),
        hasAcceptedEquityInterest: bool().required('SECURITIES_LAW'),
        acceptEquityInterestCircumstances: string().when('hasAcceptedEquityInterest', {
          is: true,
          then: sch => sch.required('SECURITIES_LAW'),
          otherwise: sch => sch.optional().nullable()
        }),
        acceptEquityInterestApprovalProcess: string().when('hasAcceptedEquityInterest', {
          is: true,
          then: sch => sch.required('SECURITIES_LAW'),
          otherwise: sch => sch.optional().nullable()
        }),
        hasProvidedLegalServicesForOfficers: bool().required('SECURITIES_LAW'),
        hasProvidedLegalServicesOwnedInterests: bool().required('SECURITIES_LAW')
      }).typeError('SECURITIES_LAW').required('SECURITIES_LAW'),
      otherwise: sch => sch.optional().nullable()
    }),
    representsClientsWithEquity: bool().required('EQUITY_INTEREST'),
    equityInterest: object().when('representsClientsWithEquity', {
      is: true,
      then: sch => sch.shape({
        providesWrittenDisclosure: bool().required('EQUITY_INTEREST'),
        acceptedEquityOfAttorneysFees: bool().required('EQUITY_INTEREST'),
        acceptedEquityOfAttorneysFeesDescription: string().when('acceptedEquityOfAttorneysFees', {
          is: true,
          then: sch => sch.required('EQUITY_INTEREST'),
          otherwise: sch => sch.nullable().notRequired()
        }),
        providedProfessionalLegalServices: bool().required('EQUITY_INTEREST'),
        providedProfessionalLegalServicesDescription: string().when('providedProfessionalLegalServices', {
          is: true,
          then: sch => sch.required('EQUITY_INTEREST'),
          otherwise: sch => sch.nullable().notRequired()
        })
      }).typeError('EQUITY_INTEREST').required('EQUITY_INTEREST'),
      otherwise: sch => sch.optional().nullable()
    }),
    hasSuedClientForFees: bool().required('LOSS_PREVENTION'),
    lossPrevention: object().when('hasSuedClientForFees', {
      is: true,
      then: sch => sch.shape({
        shareOfficeSpaceOrExpenses: bool().required('LOSS_PREVENTION'),
        sharedSpaceExpensesAttorneys: array().when('shareOfficeSpaceOrExpenses', {
          is: true,
          then: sch => sch.min(1, 'LOSS_PREVENTION').of(object({
            fullName: string().required('LOSS_PREVENTION'),
            dateSharingBegan: string().validDate('LOSS_PREVENTION').required('LOSS_PREVENTION')
          })),
          otherwise: sch => sch.optional().nullable()
        }),
        prolongedAbsenceResponsible: string().required('LOSS_PREVENTION'),
        workScheduling: array(string()).min(1, 'LOSS_PREVENTION').required('LOSS_PREVENTION'),
        workSchedulingOther: string().when('workScheduling', {
          is: (value: string[] | undefined) => value?.includes('Other') === true,
          then: sch => sch.required('LOSS_PREVENTION'),
          otherwise: sch => sch.optional().nullable()
        }),
        engagementLetters: bool().required('LOSS_PREVENTION'),
        nonEngagementLettersWhenDeclining: bool().required('LOSS_PREVENTION'),
        disengagementLettersWhenCeasing: bool().required('LOSS_PREVENTION'),
        avoidConflictOfInterests: array(string()).min(1, 'LOSS_PREVENTION').required('LOSS_PREVENTION'),
        avoidConflictOfInterestsOther: string().when('avoidConflictOfInterests', {
          is: (value: string[] | undefined) => value?.includes('Other') === true,
          then: sch => sch.required('LOSS_PREVENTION'),
          otherwise: sch => sch.optional().nullable()
        }),
        conflictWaiversPotentialConflict: bool().required('LOSS_PREVENTION'),
        conflictWaiversRepresentationAffected: bool().required('LOSS_PREVENTION'),
        conflictWaiversAnotherAttorneyConsult: bool().required('LOSS_PREVENTION'),
        timesSuedClientsForFees: number().required('LOSS_PREVENTION'),
        howManyClientsSuedFiledCounterClaim: number().required('LOSS_PREVENTION'),
      }).typeError('LOSS_PREVENTION').required('LOSS_PREVENTION'),
      otherwise: sch => sch.optional().nullable()
    }),
    hasICOCAttorneys: bool().required('IC_OC'),
    attorneys: array().when('hasICOCAttorneys', {
      is: true,
      then: sch => sch.of(object().shape({
        firstName: string().typeError('IC_OC').required('IC_OC'),
        lastName: string().typeError('IC_OC').required('IC_OC'),
        position: string().required('IC_OC'),
        yearStartedAsICOC: number().when('position', {
          is: (position: string) => position === 'Independent Contract Attorney' || position === 'Of Counsel',
          then: sch => sch.typeError('IC_OC').moreThan(1900, 'IC_OC').required('IC_OC'),
          otherwise: sch => sch.optional().nullable()
        }),
        hoursOnBill: number().when(['hoursBilledInFirstYear', 'position'], {
          is: (hoursBilledInFirstYear: number | string, position: string) => !hoursBilledInFirstYear === true && (position === 'Independent Contract Attorney' || position === 'Of Counsel'),
          then: sch => sch.moreThan(0, 'IC_OC').typeError('IC_OC').required('IC_OC'),
          otherwise: sch => sch.typeError('IC_OC').optional().nullable()
        }),
        hoursBilledInFirstYear: number().when(['hoursOnBill', 'position'], {
          is: (hoursOnBill: number | string, position: string) => !hoursOnBill === true && (position === 'Independent Contract Attorney' || position === 'Of Counsel'),
          then: sch => sch.moreThan(0, 'IC_OC').typeError('IC_OC').required('IC_OC'),
          otherwise: sch => sch.typeError('IC_OC').optional().nullable()
        }).min(1, 'IC_OC').required('IC_OC'),
      }, [['hoursOnBill', 'hoursBilledInFirstYear']]).typeError('IC_OC').required('IC_OC')).typeError('IC_OC').required('IC_OC'),
      otherwise: sch => sch.optional().nullable()
    })
  })

  const validateReviewData = () => {
    if (application?.insuranceType === 'JUDGE') {
      return []
    }
    if (updatedApplication) {
      const aopArray = updatedApplication?.firmSection?.practiceAreas ?? []
      let totalPercentage = 0
      let aopWithZeroPercentage = 0
      aopArray.forEach(item => {
        if (item.types && item.types.length > 0) {
          item.types?.forEach(it => {
            totalPercentage += (it.percentage ?? 0)
            aopWithZeroPercentage += it.percentage === 0 ? 1 : 0
          })
        } else {
          totalPercentage += (item.percentage ?? 0)
          aopWithZeroPercentage += item.percentage === 0 ? 1 : 0
        }
      })

      if (totalPercentage !== 100 || aopWithZeroPercentage > 0) {
        return ['AOP_UNDER_100']
      }

      try {
        returnReviewValidationSchema.validateSync({
          realEstate: updatedApplication?.firmSection?.practiceAreas?.some(aop => aop.area === 'Real Estate'),
          realEstateInfo: updatedApplication?.firmSection?.realEstateInfo,
          securitiesLaw: updatedApplication?.firmSection?.practiceAreas?.some(aop => aop.area === 'Securities Municipal Bonds - Tax Exempt' || aop.area === 'Securities - Other Types Of Securities Practice'),
          securitiesLawInfo: updatedApplication?.firmSection?.securitiesLawInfo,
          hasSuedClientForFees: updatedApplication.firmSection?.relationships?.hasSuedClientForFees,
          lossPrevention: updatedApplication.firmSection?.lossPrevention,
          hasICOCAttorneys: updatedApplication.attorneysSection?.hasICOCAttorneys,
          attorneys: updatedApplication.attorneysSection?.attorneys,
        }, { abortEarly: false })
        return []
      } catch (e) {
        const { errors } = e as ValidationError
        return errors
      }
    }
    return []
  }

  const returnToReview = async () => {
    const validation = validateReviewData()
    if (validation.length !== 0) {
      setShowReviewBlockMessage('open-container')
    } else if (isFormValid === undefined || isFormValid === true) {
      setIsActionBeingTaken('SAVE_REVIEW')
      setButtonsDisabled(true)
      try {
        await saveApplication({
          ...updatedApplication,
          stoppedAt: reviewMode ? reviewUrl : nextRoute
        })
        navigate(reviewUrl)
      } catch (_) { } finally {
        setIsActionBeingTaken(undefined)
        setButtonsDisabled(false)
      }
    } else {
      beforeSaveContinueClick && beforeSaveContinueClick()
    }
  }

  const handleCompleteApplication = async () => {
    if (isFormValid === undefined || isFormValid === true) {
      setIsCompletingApp(true)
      try {
        await saveApplication({
          ...updatedApplication,
          stoppedAt: reviewMode ? reviewUrl :
            application?.insuranceType && application?.insuranceType === 'LAWYER' ?
              '/sign/complete' :
              '/judge/sign/complete'
        })

        navigate(
          application?.insuranceType && application?.insuranceType === 'LAWYER' ?
            '/sign/complete' :
            '/judge/sign/complete'
        )
      } catch (_) { } finally {
        setIsCompletingApp(false)
      }
    }
  }

  const handleDismissSaveLaterMessage = () => {
    setShowSaveLaterMessage('close-container')
    setTimeout(() => {
      setShowSaveLaterMessage('close-container content-hidden')
    }, 300)
  }

  const handleDismissReviewMessage = () => {
    setShowReviewBlockMessage('close-container')
    setTimeout(() => {
      setShowReviewBlockMessage('close-container content-hidden')
    }, 300)
    beforeSaveContinueClick && beforeSaveContinueClick()
  }

  const handleDismissDuplicateQuotes = () => {
    setShowDuplicateQuotesMessage('close-container')
    setTimeout(() => {
      setShowDuplicateQuotesMessage('close-container content-hidden')
    }, 300)
    beforeSaveContinueClick && beforeSaveContinueClick()
  }

  const completeAppButtonDisabled = !isFormValid || isCompletingApp

  return (
    <>
      <ApplicationBodyFooter>
        <Button prebuilt='back' onClick={goBack} disabled={buttonsDisabled} />
        <ApplicationNextButtonsContainer>
          <ButtonWithMessageContainer>
            <div className={`savelater_message_container ${showSaveLaterMessage}`}>
              <div>
                <img src={flagImg} alt="flag" />
              </div>
              <div>
                <h5>Your application has been successfully saved.</h5>
                <p>
                  A link to continue your application will be sent to <strong>{application.applicant?.emailAddress}.</strong>
                </p>
                <p>You can access and complete your application within 30 days. After that time, you can start a new application to continue.</p>
                <p>You can safely close this browser window.</p>
                <Button
                  variant="secondary"
                  padding='8px 12px'
                  minWidth='fit-content'
                  style={{ width: 'fit-content', marginLeft: 'auto' }}
                  onClick={handleDismissSaveLaterMessage}
                >
                  Dismiss
                </Button>
              </div>
            </div>
            <Button
              prebuilt='saveLater'
              disabled={buttonsDisabled}
              onClick={saveLaterClick}
              isTakingAction={isActionBeingTaken === 'SAVE_LATER'}
              data-save-later
            />
          </ButtonWithMessageContainer>
          {reviewMode ? (
            <ButtonWithMessageContainer>
              <div className={`savelater_message_container ${showReviewBlockMessage}`}>
                <div>
                  <img src={warningImg} alt="flag" />
                </div>
                <div>
                  <h5>Missing information</h5>
                  <p>
                    Because you edited some of your answers, we need you to go forward into the application to fill out additional questions.
                  </p>
                  <p>
                    Please, click on "Save and Continue" to move on to the required page.
                  </p>
                  <Button
                    variant="secondary"
                    padding='8px 12px'
                    minWidth='fit-content'
                    style={{ width: 'fit-content', marginLeft: 'auto' }}
                    onClick={handleDismissReviewMessage}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
              <Button
                onClick={returnToReview}
                disabled={buttonsDisabled}
                isTakingAction={isActionBeingTaken === 'SAVE_REVIEW'}
              >
                {isActionBeingTaken === 'SAVE_REVIEW' ? <img src={loaderWhiteImg} alt="loading" /> : 'Return to Review'}
              </Button>
            </ButtonWithMessageContainer>
          ) : null}
          {!!nextRoute ? (
            <ButtonWithMessageContainer>
              <div className={`savelater_message_container ${showDuplicateQuotesMessage}`}>
                <div>
                  <img src={warningImg} alt="warning" />
                </div>
                <div>
                  <h5>You’ve selected duplicate quotes</h5>
                  <p>
                    You cannot submit multiple quotes with the same policy type, limit and deductible.
                  </p>
                  <Button
                    variant="secondary"
                    padding='8px 12px'
                    minWidth='fit-content'
                    style={{ width: 'fit-content', marginLeft: 'auto' }}
                    onClick={handleDismissDuplicateQuotes}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
              <Button
                variant={reviewMode ? 'plain' : 'primary'}
                disabled={buttonsDisabled || disableSaveandContinue}
                onClick={saveContinueClick}
                isTakingAction={isActionBeingTaken === 'SAVE_CONTINUE'}
                style={{ width: '220px', color: reviewMode ? '#3D6666' : '#FFF' }}
                data-save-continue
              >
                {isActionBeingTaken === 'SAVE_CONTINUE' ? <img src={loaderWhiteImg} alt="loading" /> : 'Save & Continue'}
                {isActionBeingTaken === 'SAVE_CONTINUE' ? null : <IoMdArrowForward style={{ marginLeft: '5px' }} />}
              </Button>
            </ButtonWithMessageContainer>
          ) : null}
          {!!isCompleteApplicationAvailable ? (
            <Button
              variant="primary"
              disabled={completeAppButtonDisabled}
              onClick={handleCompleteApplication}
            >
              {isCompletingApp ? <img src={loaderWhiteImg} alt="loading" /> : 'Submit Application'}
              {isCompletingApp ? null : <IoMdArrowForward style={{ marginLeft: '5px' }} />}
            </Button>
          ) : null}
        </ApplicationNextButtonsContainer>
      </ApplicationBodyFooter>
    </>
  )
}

export default PrebuiltBodyFooter